import React, { useState, useMemo, useEffect } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getFilteredRowModel,
    flexRender,
    ColumnDef,
    PaginationState
} from '@tanstack/react-table';
import { GetClientes, Cliente } from '../../services/dashboard/GetClientes';
import '../../styles/dash/BotsSection.css';
import LoadingSpinner from '../utility/LoadingSpinner';
import CreateClientModal from './CreateClientModal';
import ModalEditClient from './ModalEditClient';
import ViewChatBotsByClientModal from './ViewChatBotsByClientModal';
import { changeClientStatus } from '../../services/dashboard/ChangeClientStatus';
import { useNavigate } from 'react-router-dom';

// Interfaces para los permisos
interface Permission {
    Id: number;
    IdAction: number;
    Action: string;
    IdModule: number;
    ModuleName: string;
}

interface Role {
    Id: number;
    RoleName: string;
    Permissions: Permission[];
}

const Clients: React.FC = () => {
    const navigate = useNavigate();
    const [clients, setClients] = useState<Cliente[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [globalFilter, setGlobalFilter] = useState('');
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 10,
    });
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingClient, setEditingClient] = useState<Cliente | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showViewChatbotsModal, setShowViewChatbotsModal] = useState(false);
    const [viewingClient, setViewingClient] = useState<Cliente | null>(null);
    const [permisos, setPermisos] = useState<number[]>([]);

    // Obtener permisos del usuario desde localStorage
    useEffect(() => {
        let hasPermission = false;
        const userData = localStorage.getItem("user");
        if (userData) {
            try {
                const userObject = JSON.parse(userData);
                const userDataParsed = JSON.parse(userObject.userData || "{}");
                
                if (userDataParsed?.roles?.length) {
                    const permisosAplanados = userDataParsed.roles.flatMap((rol: Role) =>
                        rol.Permissions.map((p: Permission) => p.IdAction)
                    );
                    setPermisos(permisosAplanados);
                    if (permisosAplanados.includes(23)) {
                        hasPermission = true;
                    }
                }
            } catch (err) {
                console.error('Error parsing user data:', err);
            }
        }
        if (!hasPermission) {
            navigate("/login");
        }
    }, []);

    const fetchClients = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await GetClientes();
            setClients(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error desconocido');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchClients();
    }, []);

    const handleToggleStatus = async (id: number, currentStatus: number) => {
        // Verificar permiso para cambiar estado (ID 25)
        if (!permisos.includes(25)) {
            setError('No tienes permisos para cambiar el estado de los clientes');
            return;
        }

        const originalClients = [...clients];
        const newStatus = currentStatus === 1 ? 0 : 1;

        // Actualización optimista
        setClients(prev =>
            prev.map(client =>
                client.id === id
                    ? { ...client, status: newStatus }
                    : client
            )
        );
        setIsUpdatingStatus(true);
        setError(null);

        try {
            await changeClientStatus({ idClient: id, status: newStatus });
        } catch (err) {
            setClients(originalClients);
            setError(err instanceof Error ? err.message : 'No se pudo cambiar el estado del cliente.');
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    const handleEditClient = (client: Cliente) => {
        // Verificar permiso para editar (ID 26)
        if (!permisos.includes(26)) {
            setError('No tienes permisos para editar clientes');
            return;
        }
        
        setEditingClient(client);
        setShowEditModal(true);
    };

    const handleSaveClient = (updatedClient: Cliente) => {
        setClients(prevClients =>
            prevClients.map(client =>
                client.id === updatedClient.id ? updatedClient : client
            )
        );
    };

    const handleViewChatbots = (client: Cliente) => {
        // Verificar permiso para ver chatbots (ID 27)
        if (!permisos.includes(27)) {
            setError('No tienes permisos para ver los chatbots de los clientes');
            return;
        }
        
        setViewingClient(client);
        setShowViewChatbotsModal(true);
    };

    const columns = useMemo<ColumnDef<Cliente>[]>(() => [
        {
            id: 'actions',
            header: 'Acciones',
            cell: ({ row }) => {
                const client = row.original;
                const powerButtonColorClass = client.status === 1 ? 'power-off' : 'power-on';
                const powerButtonText = client.status === 1 ? 'Desactivar' : 'Activar';

                return (
                    <div className="table-actions">
                        <button
                            className={`action-button power ${powerButtonColorClass}`}
                            title={powerButtonText}
                            onClick={() => handleToggleStatus(client.id, client.status)}
                            disabled={isUpdatingStatus || !permisos.includes(25)}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <circle cx="12" cy="12" r="9" />
                                <line x1="12" y1="4" x2="12" y2="8" />
                            </svg>
                        </button>

                        <button
                            className="action-button edit"
                            title="Editar"
                            onClick={() => handleEditClient(client)}
                            disabled={isUpdatingStatus || !permisos.includes(26)}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="white"
                                viewBox="0 0 24 24"
                            >
                                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" />
                                <path d="M20.71 7.04a1.003 1.003 0 0 0 0-1.41l-2.34-2.34a1.003 1.003 0 0 0-1.41 0L15.13 4.9l3.75 3.75 1.83-1.61z" />
                            </svg>
                        </button>

                        <button
                            className="action-button view"
                            title="Ver Chatbots"
                            onClick={() => handleViewChatbots(client)}
                            disabled={isUpdatingStatus || !permisos.includes(27)}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="currentColor"
                                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                            </svg>
                        </button>

                    </div>
                );
            },
        },
        {
            accessorKey: 'nombre',
            header: 'Nombre',
            cell: info => info.getValue(),
        },
        {
            accessorKey: 'correo',
            header: 'Email',
            cell: info => info.getValue(),
        },
        {
            accessorKey: 'telefono', // Mayúscula
            header: 'Teléfono',
            cell: info => info.getValue(),
        },
        {
            accessorKey: 'status',
            header: 'Estado',
            cell: info => {
                const status = info.getValue<number>();
                const statusText = status === 1 ? 'Activo' : 'Inactivo';
                return (
                    <span className={`status-text ${statusText.toLowerCase()}`}>
                        {statusText}
                    </span>
                );
            },
        },
    ], [isUpdatingStatus, permisos]);

    const table = useReactTable({
        data: clients,
        columns,
        state: {
            globalFilter,
            pagination,
        },
        onGlobalFilterChange: setGlobalFilter,
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    function getPaginationRange(current: number, total: number): (number | string)[] {
        if (total <= 1) return [1];

        const delta = 1;
        const range: (number | string)[] = [];
        const rangeWithDots: (number | string)[] = [];
        let l = 0;

        for (let i = 1; i <= total; i++) {
            if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
                range.push(i);
            }
        }

        for (let i of range) {
            if (l > 0) {
                if (Number(i) - l === 2) {
                    rangeWithDots.push(l + 1);
                } else if (Number(i) - l !== 1) {
                    rangeWithDots.push("...");
                }
            }
            rangeWithDots.push(i);
            l = Number(i);
        }

        return rangeWithDots;
    }

    return (
        <div className="dashboard-container">
            <div className="demo-request-table-container t">
                <div className="table-header-toolbar">
                    <button 
                        className="add-bot-tab-button" 
                        onClick={() => setShowCreateModal(true)}
                        disabled={!permisos.includes(24)} // ID 24 para crear clientes
                    >
                        Crear Nuevo Cliente
                    </button>

                    <div className="search-container">
                        <input
                            type="text"
                            placeholder="Buscar clientes..."
                            value={globalFilter}
                            onChange={(e) => setGlobalFilter(e.target.value)}
                            className="global-filter"
                        />
                    </div>
                </div>

                {loading ? (
                    <LoadingSpinner />
                ) : error ? (
                    <div className="error-message">Error: {error}</div>
                ) : clients.length === 0 ? (
                    <div className="no-data-message">No se encontraron clientes</div>
                ) : (
                    <>
                        {isUpdatingStatus && (
                            <div className="overlay-spinner">
                                <LoadingSpinner />
                                
                            </div>
                        )}

                        <div className="chatbots-table-container">
                            <table className="chatbots-table">
                                <colgroup>
                                    <col style={{ width: '15%' }} /><col style={{ width: '25%' }} /><col style={{ width: '25%' }} /><col style={{ width: '20%' }} /><col style={{ width: '15%' }} />
                                </colgroup>
                                <thead>
                                    {table.getHeaderGroups().map(headerGroup => (
                                        <tr key={headerGroup.id}>
                                            {headerGroup.headers.map(header => (
                                                <th key={header.id}>
                                                    {flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )}
                                                </th>
                                            ))}
                                        </tr>
                                    ))}
                                </thead>
                                <tbody>
                                    {table.getRowModel().rows.map(row => (
                                        <tr key={row.id}>
                                            {row.getVisibleCells().map(cell => (
                                                <td key={cell.id}>
                                                    {flexRender(
                                                        cell.column.columnDef.cell,
                                                        cell.getContext()
                                                    )}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="pagination-controls">
                            <button
                                onClick={() => table.previousPage()}
                                disabled={!table.getCanPreviousPage() || isUpdatingStatus}
                            >
                                ←
                            </button>

                            {getPaginationRange(
                                table.getState().pagination.pageIndex + 1,
                                table.getPageCount()
                            ).map((page, idx) =>
                                page === "..." ? (
                                    <span key={idx} className="dots">...</span>
                                ) : (
                                    <button
                                        key={idx}
                                        className={
                                            table.getState().pagination.pageIndex + 1 === page
                                                ? "active-page"
                                                : ""
                                        }
                                        onClick={() => table.setPageIndex(Number(page) - 1)}
                                        disabled={isUpdatingStatus}
                                    >
                                        {page}
                                    </button>
                                )
                            )}

                            <button
                                onClick={() => table.nextPage()}
                                disabled={!table.getCanNextPage() || isUpdatingStatus}
                            >
                                →
                            </button>
                        </div>
                    </>
                )}
            </div>

            {showEditModal && (
                <ModalEditClient
                    client={editingClient}
                    show={showEditModal}
                    onClose={() => {
                        setShowEditModal(false);
                        setEditingClient(null);
                    }}
                    onSave={handleSaveClient}
                />
            )}

            {showCreateModal && (
                <CreateClientModal
                    show={showCreateModal}
                    onClose={() => setShowCreateModal(false)}
                    onSuccess={() => {
                        setShowCreateModal(false);
                        fetchClients();
                    }}
                />
            )}

            {showViewChatbotsModal && viewingClient && (
                <ViewChatBotsByClientModal
                    show={showViewChatbotsModal}
                    onClose={() => {
                        setShowViewChatbotsModal(false);
                        setViewingClient(null);
                    }}
                    clientId={viewingClient.id}
                    clientName={viewingClient.nombre}
                />
            )}
        </div>
    );
};

export default Clients;